'use client';

import type { SnackbarProps } from '@musinsa/mds-ui';
import { useSnackbar as useBaseSnackbar } from '@musinsa/mds-ui';
import { SERVICE } from '@musinsa/packages-device-detection';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useAppInterface } from '../AppInterfaceProvider';

type CuratorSnackbarOptions = Omit<SnackbarProps, 'id'> & {
  shouldToastFloatAboveTabBar?: boolean;
};

const canUseNativeToast = (options: CuratorSnackbarOptions) =>
  !options.action && !options.onClick && !options.image && options.autoClose !== false;

const callOnCloseSafely = (onClose?: () => void) => {
  if (!onClose) {
    return;
  }

  if (typeof queueMicrotask === 'function') {
    queueMicrotask(onClose);
    return;
  }

  setTimeout(onClose, 0);
};

export const useCuratorSnackbar = () => {
  const baseSnackbar = useBaseSnackbar();
  const { service: appService, methods } = useAppInterface();
  const { show: baseShow, close, closeAll, snackbars } = baseSnackbar;

  const hasNativeToast = appService !== SERVICE.MUSINSA && methods?.showToast;

  const show = useCallback(
    (options: CuratorSnackbarOptions) => {
      const canUseNative = canUseNativeToast(options) && hasNativeToast;
      const { shouldToastFloatAboveTabBar, ...restOptions } = options;

      if (canUseNative) {
        if (appService === SERVICE['29CM']) {
          methods.showToast({
            title: options.title ?? options.message,
            shouldToastFloatAboveTabBar,
          });
        }

        callOnCloseSafely(options.onClose);
        return `native-toast-${Date.now()}`;
      }

      return baseShow(restOptions);
    },
    [baseShow, methods],
  );

  const success = useCallback(
    (message: string, options?: Partial<Omit<CuratorSnackbarOptions, 'message' | 'variant'>>) =>
      show({ ...(options ?? {}), message, variant: 'success' }),
    [show],
  );

  const error = useCallback(
    (message: string, options?: Partial<Omit<CuratorSnackbarOptions, 'message' | 'variant'>>) =>
      show({ ...(options ?? {}), message, variant: 'error' }),
    [show],
  );

  const warning = useCallback(
    (message: string, options?: Partial<Omit<CuratorSnackbarOptions, 'message' | 'variant'>>) =>
      show({ ...(options ?? {}), message, variant: 'warning' }),
    [show],
  );

  const defaultVariant = useCallback(
    (message: string, options?: Partial<Omit<CuratorSnackbarOptions, 'message' | 'variant'>>) =>
      show({ ...(options ?? {}), message, variant: 'default' }),
    [show],
  );

  const product = useCallback(
    (
      title: string,
      message: string,
      image: string,
      options?: Partial<Omit<CuratorSnackbarOptions, 'title' | 'message' | 'image'>>,
    ) => show({ ...(options ?? {}), title, message, image }),
    [show],
  );

  const withNavigation = useCallback(
    (
      message: string,
      onNavigate: () => void,
      options?: Partial<Omit<CuratorSnackbarOptions, 'message' | 'action'>>,
    ) =>
      show({
        ...(options ?? {}),
        message,
        action: { onAction: onNavigate },
      }),
    [show],
  );

  const withTextAction = useCallback(
    (
      message: string,
      actionText: string,
      onAction: () => void,
      options?: Partial<Omit<CuratorSnackbarOptions, 'message' | 'action'>>,
    ) =>
      show({
        ...(options ?? {}),
        message,
        action: { text: actionText, onAction },
      }),
    [show],
  );

  const withIconAction = useCallback(
    (
      message: string,
      icon: ReactNode,
      onAction: () => void,
      options?: Partial<Omit<CuratorSnackbarOptions, 'message' | 'action'>>,
    ) =>
      show({
        ...(options ?? {}),
        message,
        action: { icon, onAction },
      }),
    [show],
  );

  return {
    show,
    close,
    closeAll,
    snackbars,
    success,
    error,
    warning,
    default: defaultVariant,
    product,
    withNavigation,
    withTextAction,
    withIconAction,
  };
};
